import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { Product } from "@/types/products";

import { useCategories } from "@/api/category-query";
import { usePrivateAxios } from "@/api/use-private-axios";
import { useCurrencies } from "@/api/util";
import UploadFile from "@/components/form/file-input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumInputSeparated } from "@/components/ui/num-input-separated";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/store/create")({
	component: CreatePage
});

const createProductSchema = z.object({
	name: z.string({
		required_error: "Name is required"
	}).min(3, {
		message: "Name must be at least 3 characters"
	}),
	price: z.coerce.string({
		required_error: "Price is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Price must be a number greater than 0"
	}),
	status: z.enum(["ACTIVE", "INACTIVE"], {
		required_error: "Status is required"
	}).default("ACTIVE"),
	quantity: z.coerce.string({
		required_error: "Quantity is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Quantity must be a number greater than 0"
	}),
	description: z.string({
		required_error: "Description is required"
	}).min(10, {
		message: "Description must be at least 10 characters"
	}),
	photos: z.string({ required_error: "At least one photo is required" }).min(1, {
		message: "At least one photo is required"
	}),
	categoryIds: z.array(z.string()).min(1, {
		message: "At least one category is required"
	})
});

function CreatePage() {
	const privateAxios = usePrivateAxios();

	const [files, setFiles] = useState<File[] | null>(null);

	const form = useForm<z.infer<typeof createProductSchema>>(
		{
			resolver: zodResolver(createProductSchema),
			defaultValues: {
				name: "",
				description: "",
				photos: files ? files[0]?.name : "",
				price: "",
				quantity: "",
				status: "ACTIVE",
				categoryIds: []
			}
		}
	);
	const createProductMutation = useMutation(
		{
			mutationKey: ["create-product"],
			mutationFn: async (data: FormData) => {
				const response = await privateAxios.post<{ data: Product }>("/product/create_product", data, {
					headers: {
						"Content-Type": "multipart/form-data"
					}
				});
				return response.data;
			},
			onSuccess: (data) => {
				toast.success("Product created successfully", {
					action: (
						<Button variant="secondary" asChild>
							<Link to="/vendor/store/$productid" params={{ productid: data.data.id }}>
								View Product
							</Link>
						</Button>
					)
				});
				setFiles(null);
				form.reset();
			},
			onError: error => toast.error("Failed to create product", {
				description: (error as any).response?.data.message
			})

		}
	);

	const categoryQuery = useCategories();
	const currenciesQuery = useCurrencies();

	const handleSubmit = async (data: z.infer<typeof createProductSchema>) => {
		if (!files)
			return;
		const product = new FormData();
		if (files) {
			files.forEach((file, index) => {
				product.append(`files`, file, `image${index}.jpg`);
			});
		}
		Object.entries(data).forEach(([key, value]) => {
			if (key !== "photos") {
				if (Array.isArray(value)) {
					const categoryIds = value.pop();
					if (categoryIds)
						product.append("categoryIds", String(categoryIds));
					// value.forEach(v => product.append(key, v));
				}
				else {
					product.append(key, String(value));
				}
			}
		});

		const currency = currenciesQuery.data?.data.find(currency => currency.name === "Naira");

		if (currency) {
			product.append("currencyId", String(currency.id));
		}

		await createProductMutation.mutateAsync(product);
	};

	return (

		<section className="flex flex-col space-y-4">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit)}
					className={cn("min-h-[80vh] p-3 bg-white rounded-xl flex flex-col gap-4 flex-wrap h-full", {
					})}
				>
					<h3>Product images</h3>
					<UploadFile
						files={files}
						setFiles={setFiles}
						className="outline-dashed outline-1 outline-primary hover:bg-primary/20"
						name="photos"
						control={form.control}
					/>
					<span className="text-muted-foreground text-sm">(Recommended dimension 930 x 1163)</span>
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input placeholder="Enter product name" className="h-12" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="price"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<NumInputSeparated
										currency="NGN"
										placeholder="Enter price"
										value={field.value}
										onChange={field.onChange}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="quantity"
						render={({ field }) => {
							return (
								<FormItem>
									<FormControl>
										<NumInputSeparated
											placeholder="Enter quantity"
											value={field.value}
											onChange={field.onChange}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							);
						}}
					/>
					{/* <FormField
						control={form.control}
						name="status"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Select
										defaultValue={field.value}
										onValueChange={value => field.onChange(value)}
										value={field.value}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select a status" />
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="ACTIVE">Active</SelectItem>
												<SelectItem value="INACTIVE">In Active</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/> */}
					<FormField
						control={form.control}
						name="categoryIds"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<div className="flex flex-wrap gap-2">
										{categoryQuery.isPending
											? (
													<>
														<Skeleton className="w-20 h-6 animate-pulse" />
														<Skeleton className="w-20 h-6 animate-pulse" />
														<Skeleton className="w-20 h-6 animate-pulse" />
														<Skeleton className="w-20 h-6 animate-pulse" />
														<Skeleton className="w-20 h-6 animate-pulse" />
													</>
												)
											: (
													categoryQuery.data?.data.map(category => (
														<div
															tabIndex={0}
															role="button"
															key={category.id}
															className={cn(
																"bg-primary/10 p-1 px-2 border border-primary rounded-2xl cursor-pointer",
																{
																	"bg-primary text-white": field.value?.includes(category.id)
																}
															)}
															onClick={() => {
																const values = field.value || [];
																const newValues = values.includes(category.id)
																	? values.filter(id => id !== category.id)
																	: [...values, category.id];
																field.onChange(newValues);
															}}
														>
															<p className="text-sm">{category.title}</p>
														</div>
													))
												)}
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Textarea placeholder="Enter product description" className="min-h-[20vh] text-sm" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex justify-between">
						<Dialog>
							<DialogTrigger asChild type="button">
								<Button variant="link">How to use</Button>
							</DialogTrigger>
							<DialogContent className="min-h-[80vh] max-w-4xl">
								<DialogHeader>
									<DialogTitle>How to use</DialogTitle>
									<DialogDescription>
										How to add a product to your store
									</DialogDescription>
									<div className="flex flex-col gap-2 mt-10">
										<video
											className="w-full rounded-lg"
											controls
										>
											<source src="/guide.webm" />
											Your browser does not support the video tag.
										</video>
									</div>
								</DialogHeader>
							</DialogContent>
						</Dialog>
						<span className="flex gap-2 ml-auto">
							<Button type="submit" disabled={createProductMutation.isPending}>
								{createProductMutation.isPending
									? (
											<>
												<Loader2 size={20} className="animate-spin" />
												&nbsp; Loading....
											</>
										)

									: "Save Product"}
							</Button>
							<Button
								disabled={createProductMutation.isPending}
								onClick={() => {
									form.reset();
									history.back();
								}}
								type="button"
								className="bg-destructive-secondary gap-2 text-destructive hover:bg-destructive-secondary/50"
							>
								<X className="size-4" />
								Cancel
							</Button>
						</span>
					</div>
				</form>
			</Form>
		</section>
	);
}
